/**
 * VSS Noise Field — live noise grid.
 *
 * Progressive enhancement for the 32-type noise grid on the product page.
 * The static webp grid is the baseline; this replaces tiles with live
 * WebGL2 renders of the same shader (1:1 port of the FFGL fragment source
 * in vss-FFGL-Noise) only after the context and program initialise.
 * Palette swatches above the grid re-render every tile in the chosen
 * palette. One shared WebGL2 context renders each tile once per palette
 * selection (render-once, static after), fed to per-tile 2D canvases.
 * Tiles render on demand as they scroll into view, budgeted per frame.
 */
(function () {
  'use strict';

  window.__vssNoiseLive = 'loaded';

  var section = document.querySelector('[data-noise-live]');
  if (!section) return;
  window.__vssNoiseLive = 'section-found';

  var tiles = Array.prototype.slice.call(section.querySelectorAll('[data-noise-tile]'));
  if (!tiles.length) return;

  var TILE_SIZE = 400;
  var FRAME_BUDGET_MS = 8;
  var MAX_TILES_PER_FRAME = 4;

  // Global Scale slider values, mapped like the plugin's ProcessOpenGL.
  // Types 3 and 4 use the documented zoomed-in setting (slider 0.08).
  var DEFAULT_GSCALE_SLIDER = 0.30;
  var TYPE_GSCALE_SLIDER = { 3: 0.08, 4: 0.08 };

  // Fallback stops when no palette swatches exist: VSS Signature.
  var SIGNATURE_STOPS = ['#151515', '#FF3701', '#F98441', '#747C51', '#EDEDED'];

  var VERT = '#version 300 es\n' +
    'in vec4 vPosition;\n' +
    'void main() { gl_Position = vPosition; }\n';

  // Fragment shader: segments A-F are the untouched port of the FFGL source;
  // the palette table is replaced by a uniform vec3[5] so any palette from
  // the store data renders without recompiling.
  var FRAG =
    '#version 300 es\n' +
    'precision highp float;\n' +
    'precision highp int;\n' +
    'uniform vec2  u_res;\n' +
    'uniform vec3  u_loop;\n' +
    'uniform float u_gScale;\n' +
    'uniform vec2  u_scale;\n' +
    'uniform int   u_type;\n' +
    'uniform int   u_oct;\n' +
    'uniform float u_gain;\n' +
    'uniform float u_lac;\n' +
    'uniform int   u_seed;\n' +
    'uniform float u_lo;\n' +
    'uniform float u_hi;\n' +
    'uniform float u_bri;\n' +
    'uniform float u_con;\n' +
    'uniform int   u_abs;\n' +
    'uniform float u_cyc;\n' +
    'uniform vec2  u_ofs;\n' +
    'uniform float u_warp;\n' +
    'uniform int   u_col;\n' +
    'uniform float u_cph;\n' +
    'uniform float u_correction;\n' +
    'uniform vec3  u_pal[5];\n' +
    'out vec4 fragColor;\n' +
    'float h1(vec3 p) {\n' +
    '    p = fract(p * vec3(0.1031, 0.1030, 0.0973));\n' +
    '    p += dot(p, p.yxz + 33.33);\n' +
    '    return fract((p.x + p.y) * p.z);\n' +
    '}\n' +
    'vec3 h3(vec3 p) {\n' +
    '    p = vec3(dot(p, vec3(127.1, 311.7,  74.7)),\n' +
    '             dot(p, vec3(269.5, 183.3, 246.1)),\n' +
    '             dot(p, vec3(113.5, 271.9, 124.6)));\n' +
    '    return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);\n' +
    '}\n' +
    'vec3 h3u(vec3 p) { return h3(p) * 0.5 + 0.5; }\n' +
    'float vn(vec3 p) {\n' +
    '    vec3 i = floor(p), f = fract(p);\n' +
    '    vec3 u = f * f * (3.0 - 2.0 * f);\n' +
    '    return mix(\n' +
    '        mix(mix(h1(i),               h1(i+vec3(1,0,0)), u.x),\n' +
    '            mix(h1(i+vec3(0,1,0)),   h1(i+vec3(1,1,0)), u.x), u.y),\n' +
    '        mix(mix(h1(i+vec3(0,0,1)),   h1(i+vec3(1,0,1)), u.x),\n' +
    '            mix(h1(i+vec3(0,1,1)),   h1(i+vec3(1,1,1)), u.x), u.y), u.z);\n' +
    '}\n' +
    'float svn(vec3 p) {\n' +
    '    vec3 i = floor(p), f = fract(p);\n' +
    '    vec3 u = f*f*f*(f*(f*6.0-15.0)+10.0);\n' +
    '    return mix(\n' +
    '        mix(mix(h1(i),               h1(i+vec3(1,0,0)), u.x),\n' +
    '            mix(h1(i+vec3(0,1,0)),   h1(i+vec3(1,1,0)), u.x), u.y),\n' +
    '        mix(mix(h1(i+vec3(0,0,1)),   h1(i+vec3(1,0,1)), u.x),\n' +
    '            mix(h1(i+vec3(0,1,1)),   h1(i+vec3(1,1,1)), u.x), u.y), u.z);\n' +
    '}\n' +
    'float gn(vec3 p) {\n' +
    '    vec3 i = floor(p), f = fract(p);\n' +
    '    vec3 u = f*f*f*(f*(f*6.0-15.0)+10.0);\n' +
    '    float n = mix(\n' +
    '        mix(mix(dot(h3(i),             f),\n' +
    '                dot(h3(i+vec3(1,0,0)), f-vec3(1,0,0)), u.x),\n' +
    '            mix(dot(h3(i+vec3(0,1,0)), f-vec3(0,1,0)),\n' +
    '                dot(h3(i+vec3(1,1,0)), f-vec3(1,1,0)), u.x), u.y),\n' +
    '        mix(mix(dot(h3(i+vec3(0,0,1)), f-vec3(0,0,1)),\n' +
    '                dot(h3(i+vec3(1,0,1)), f-vec3(1,0,1)), u.x),\n' +
    '            mix(dot(h3(i+vec3(0,1,1)), f-vec3(0,1,1)),\n' +
    '                dot(h3(i+vec3(1,1,1)), f-vec3(1,1,1)), u.x), u.y), u.z);\n' +
    '    return clamp(n * 0.5 + 0.5, 0.0, 1.0);\n' +
    '}\n' +
    'vec3 s_m289v3(vec3 x) { return x - floor(x*(1.0/289.0))*289.0; }\n' +
    'vec4 s_m289v4(vec4 x) { return x - floor(x*(1.0/289.0))*289.0; }\n' +
    'vec4 s_perm(vec4 x)   { return s_m289v4((x*34.0+1.0)*x); }\n' +
    'vec4 s_tis(vec4 r)    { return 1.79284291400159 - 0.85373472095314*r; }\n' +
    'float sn(vec3 v) {\n' +
    '    const vec2 C = vec2(1.0/6.0, 1.0/3.0);\n' +
    '    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);\n' +
    '    vec3 i  = floor(v + dot(v, C.yyy));\n' +
    '    vec3 x0 = v - i + dot(i, C.xxx);\n' +
    '    vec3 g  = step(x0.yzx, x0.xyz), l = 1.0 - g;\n' +
    '    vec3 i1 = min(g.xyz, l.zxy), i2 = max(g.xyz, l.zxy);\n' +
    '    vec3 x1 = x0-i1+C.xxx, x2 = x0-i2+C.yyy, x3 = x0-D.yyy;\n' +
    '    i = s_m289v3(i);\n' +
    '    vec4 p = s_perm(s_perm(s_perm(\n' +
    '        i.z+vec4(0.0,i1.z,i2.z,1.0))+\n' +
    '        i.y+vec4(0.0,i1.y,i2.y,1.0))+\n' +
    '        i.x+vec4(0.0,i1.x,i2.x,1.0));\n' +
    '    float n_ = 0.142857142857;\n' +
    '    vec3  ns = n_*D.wyz - D.xzx;\n' +
    '    vec4  j  = p - 49.0*floor(p*ns.z*ns.z);\n' +
    '    vec4  x_ = floor(j*ns.z), y_ = floor(j-7.0*x_);\n' +
    '    vec4  xf = x_*ns.x+ns.yyyy, yf = y_*ns.x+ns.yyyy;\n' +
    '    vec4  h  = 1.0-abs(xf)-abs(yf);\n' +
    '    vec4  b0 = vec4(xf.xy,yf.xy), b1 = vec4(xf.zw,yf.zw);\n' +
    '    vec4  s0 = floor(b0)*2.0+1.0, s1 = floor(b1)*2.0+1.0;\n' +
    '    vec4  sh = -step(h, vec4(0.0));\n' +
    '    vec4  a0 = b0.xzyw+s0.xzyw*sh.xxyy, a1 = b1.xzyw+s1.xzyw*sh.zzww;\n' +
    '    vec3  p0 = vec3(a0.xy,h.x), p1 = vec3(a0.zw,h.y);\n' +
    '    vec3  p2 = vec3(a1.xy,h.z), p3 = vec3(a1.zw,h.w);\n' +
    '    vec4  norm = s_tis(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));\n' +
    '    p0*=norm.x; p1*=norm.y; p2*=norm.z; p3*=norm.w;\n' +
    '    vec4 m = max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);\n' +
    '    m = m*m;\n' +
    '    return clamp(42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)))*0.5+0.5,0.0,1.0);\n' +
    '}\n' +
    'vec2 vor(vec3 p) {\n' +
    '    vec3 n = floor(p), f = fract(p);\n' +
    '    float F1 = 8.0, F2 = 8.0;\n' +
    '    for (int k=-1;k<=1;k++) for (int j=-1;j<=1;j++) for (int ii=-1;ii<=1;ii++) {\n' +
    '        vec3 g = vec3(ii,j,k);\n' +
    '        vec3 r = g + h3u(n+g) - f;\n' +
    '        float d = dot(r,r);\n' +
    '        if (d<F1) { F2=F1; F1=d; } else if (d<F2) { F2=d; }\n' +
    '    }\n' +
    '    return vec2(sqrt(F1), sqrt(F2));\n' +
    '}\n' +
    'float fbmG(vec3 p,int o,float g,float l){\n' +
    '    float v=0.0,a=0.5,f=1.0,s=0.0;\n' +
    '    for(int i=0;i<o;i++){v+=a*gn(p*f);s+=a;f*=l;a*=g;} return v/s;\n' +
    '}\n' +
    'float fbmV(vec3 p,int o,float g,float l){\n' +
    '    float v=0.0,a=0.5,f=1.0,s=0.0;\n' +
    '    for(int i=0;i<o;i++){v+=a*vn(p*f);s+=a;f*=l;a*=g;} return v/s;\n' +
    '}\n' +
    'float fbmS(vec3 p,int o,float g,float l){\n' +
    '    float v=0.0,a=0.5,f=1.0,s=0.0;\n' +
    '    for(int i=0;i<o;i++){v+=a*sn(p*f);s+=a;f*=l;a*=g;} return v/s;\n' +
    '}\n' +
    'float fbmT(vec3 p,int o,float g,float l){\n' +
    '    float v=0.0,a=0.5,f=1.0,s=0.0;\n' +
    '    for(int i=0;i<o;i++){v+=a*abs(gn(p*f)*2.0-1.0);s+=a;f*=l;a*=g;} return v/s;\n' +
    '}\n' +
    'float fbmB(vec3 p,int o,float g,float l){\n' +
    '    float v=0.0,a=0.5,f=1.0,s=0.0;\n' +
    '    for(int i=0;i<o;i++){v+=a*(1.0-abs(gn(p*f)*2.0-1.0));s+=a;f*=l;a*=g;} return v/s;\n' +
    '}\n' +
    'float evalNoise(vec3 p,int o,float g,float l) {\n' +
    '    switch(u_type) {\n' +
    '    case 0: {\n' +
    '        float base = fbmT(p, o, g, l);\n' +
    '        return pow(base, 4.0);\n' +
    '    }\n' +
    '    case 1: {\n' +
    '        return h1(floor(p));\n' +
    '    }\n' +
    '    case 2: {\n' +
    '        return pow(sn(p), 8.0) * 0.3;\n' +
    '    }\n' +
    '    case 3: {\n' +
    '        vec2 va = vor(p * 1.0);\n' +
    '        vec2 vb = vor(p * 1.0 + vec3(0.7, 1.3, 0.0));\n' +
    '        float cluster = min(va.x, vb.x);\n' +
    '        float spot = 1.0 - smoothstep(0.0, 0.2, cluster);\n' +
    '        float spot2 = (1.0 - smoothstep(0.0, 0.15, va.x)) * 0.6;\n' +
    '        return clamp(1.0 - spot * 0.9 - spot2 * 0.5, 0.0, 1.0);\n' +
    '    }\n' +
    '    case 4: {\n' +
    '        vec2 disp = vec2((sn(p * 0.6) * 2.0 - 1.0) * 0.3, (sn(p * 0.6 + vec3(5.1, 0.0, 0.0)) * 2.0 - 1.0) * 0.3);\n' +
    '        vec2 va = vor(p * 1.2 + vec3(disp, 0.0));\n' +
    '        vec2 vb = vor(p * 1.2 + vec3(disp * 0.5 + vec2(1.3, 0.7), 0.0));\n' +
    '        float spot = 1.0 - smoothstep(0.0, 0.22, va.x);\n' +
    '        float spot2 = (1.0 - smoothstep(0.0, 0.18, vb.x)) * 0.7;\n' +
    '        return clamp(1.0 - spot * 0.85 - spot2 * 0.5, 0.0, 1.0);\n' +
    '    }\n' +
    '    case 5: {\n' +
    '        vec2 f12 = vor(p * 1.0);\n' +
    '        float petal = pow(clamp(f12.y - f12.x, 0.0, 1.0), 0.5);\n' +
    '        float soft = smoothstep(0.0, 0.6, petal);\n' +
    '        return 0.85 + soft * 0.12;\n' +
    '    }\n' +
    '    case 6: {\n' +
    '        vec2 f12 = vor(p * 1.0);\n' +
    '        float edge = f12.y - f12.x;\n' +
    '        float wedge = smoothstep(0.05, 0.35, edge);\n' +
    '        return wedge * wedge * 0.95;\n' +
    '    }\n' +
    '    case 7: {\n' +
    '        float n = sn(p * 1.5);\n' +
    '        float grad = length(vec2(dFdx(n), dFdy(n)));\n' +
    '        return smoothstep(0.04, 0.12, grad * 20.0) * 0.9;\n' +
    '    }\n' +
    '    case 8: {\n' +
    '        float v = 0.0, a = 0.5, fr = 1.0, mx = 0.0;\n' +
    '        for (int i = 0; i < o; i++) {\n' +
    '            float n = 1.0 - abs(sn(p * fr) * 2.0 - 1.0);\n' +
    '            n = pow(n, 1.5);\n' +
    '            v += a * n; mx += a; a *= g; fr *= 2.0;\n' +
    '        }\n' +
    '        return 1.0 - smoothstep(0.3, 0.7, v / mx);\n' +
    '    }\n' +
    '    case 9: {\n' +
    '        vec3 q = vec3(\n' +
    '            sn(p) * 2.0 - 1.0,\n' +
    '            sn(p + vec3(3.7, 0.0, 0.0)) * 2.0 - 1.0,\n' +
    '            0.0);\n' +
    '        float base = sn(p + q * 1.5);\n' +
    '        float band = abs(fract(base * 6.0) - 0.5) * 2.0;\n' +
    '        float line = 1.0 - smoothstep(0.0, 0.25, band);\n' +
    '        return mix(base * 0.55 + 0.15, 0.9, line);\n' +
    '    }\n' +
    '    case 10: {\n' +
    '        int wo = min(o, 3);\n' +
    '        vec3 q = vec3(\n' +
    '            fbmV(p,                        wo, g, l) - 0.5,\n' +
    '            fbmV(p + vec3(3.1, 0.0, 0.0), wo, g, l) - 0.5,\n' +
    '            fbmV(p + vec3(0.0, 3.1, 0.0), wo, g, l) - 0.5) * 0.4;\n' +
    '        return fbmT(p + q, o, g, l) * 0.3 + 0.6;\n' +
    '    }\n' +
    '    case 11: {\n' +
    '        float pot1 = sn(p);\n' +
    '        float pot2 = sn(p + vec3(2.3, 1.7, 0.0));\n' +
    '        float b1 = abs(fract(pot1 * 5.0) - 0.5) * 2.0;\n' +
    '        float b2 = abs(fract(pot2 * 5.0) - 0.5) * 2.0;\n' +
    '        float ring = max(1.0 - smoothstep(0.0, 0.18, b1),\n' +
    '                         1.0 - smoothstep(0.0, 0.18, b2));\n' +
    '        float base = pot1 * 0.15 + 0.7;\n' +
    '        return mix(base, 0.0, ring * 0.95);\n' +
    '    }\n' +
    '    case 12: {\n' +
    '        float v=0.0,a=0.5,f=1.0,s=0.0;\n' +
    '        for(int i=0;i<o;i++){\n' +
    '            vec3 fp=p*f; fp.y*=2.4;\n' +
    '            v+=a*(1.0-abs(gn(fp)*2.0-1.0));s+=a;f*=l;a*=g;\n' +
    '        }\n' +
    '        return v/s;\n' +
    '    }\n' +
    '    case 13: {\n' +
    '        float b = fbmB(p * 0.5, o, g, l);\n' +
    '        b = smoothstep(0.25, 0.85, b);\n' +
    '        return b * 0.8 + 0.1;\n' +
    '    }\n' +
    '    case 14: {\n' +
    '        vec2 f12 = vor(p * 1.8);\n' +
    '        float spot = 1.0 - smoothstep(0.0, 0.15, f12.x);\n' +
    '        return 0.85 - spot * 0.6;\n' +
    '    }\n' +
    '    case 15: {\n' +
    '        float n1 = sn(p * 1.2);\n' +
    '        float n2 = sn(p * 2.4 + vec3(1.7, 3.2, 0.0));\n' +
    '        float g1 = length(vec2(dFdx(n1), dFdy(n1)));\n' +
    '        float g2 = length(vec2(dFdx(n2), dFdy(n2)));\n' +
    '        float marks = smoothstep(0.03, 0.09, g1 * 18.0)\n' +
    '                    + smoothstep(0.02, 0.07, g2 * 14.0) * 0.5;\n' +
    '        return clamp(marks, 0.0, 1.0) * 0.85;\n' +
    '    }\n' +
    '    case 16: {\n' +
    '        float micro = sn(p * 8.0) * 0.15;\n' +
    '        float macro = fbmS(p, o, g, l);\n' +
    '        float bright = pow(macro, 3.0) * 1.5;\n' +
    '        return clamp(micro * 0.4 + 0.35 + bright, 0.0, 1.0);\n' +
    '    }\n' +
    '    case 17: {\n' +
    '        float base = fbmB(p, o, g, l);\n' +
    '        float micro = sn(p * 6.0) * 0.1;\n' +
    '        return clamp(base * 0.7 + micro + 0.1, 0.0, 1.0);\n' +
    '    }\n' +
    '    case 18: {\n' +
    '        float n = sn(p);\n' +
    '        float band = abs(fract(n * 4.0) - 0.5) * 2.0;\n' +
    '        return 1.0 - smoothstep(0.0, 0.15, band);\n' +
    '    }\n' +
    '    case 19: {\n' +
    '        return fbmG(p, o, g, l);\n' +
    '    }\n' +
    '    case 20: {\n' +
    '        float base = fbmS(p * 0.5, min(o, 4), g * 0.6, l);\n' +
    '        return base * 0.2 + 0.75;\n' +
    '    }\n' +
    '    case 21: {\n' +
    '        return sn(p * 0.4) * 0.08 + 0.88;\n' +
    '    }\n' +
    '    case 22: {\n' +
    '        vec2 f12 = vor(p * 1.2);\n' +
    '        float cell = 1.0 - smoothstep(0.0, 0.5, f12.x);\n' +
    '        return cell * 0.4 + 0.4;\n' +
    '    }\n' +
    '    case 23: {\n' +
    '        float base = fbmV(p * 0.7, min(o, 3), g * 0.5, l);\n' +
    '        return base * 0.12 + 0.82;\n' +
    '    }\n' +
    '    case 24: {\n' +
    '        float n = sn(p * 0.8) * 2.0 - 1.0;\n' +
    '        float veins = sin((p.x * 2.0 + n * 4.0) * 3.14159);\n' +
    '        veins = abs(veins);\n' +
    '        veins = pow(veins, 0.5);\n' +
    '        return veins;\n' +
    '    }\n' +
    '    case 25: {\n' +
    '        float n1=sn(p); float n2=sn(p*2.1+vec3(1.7,3.1,0.0)); float n3=sn(p*3.7+vec3(4.2,1.5,0.0));\n' +
    '        float b1=abs(fract(n1*3.0)-0.5)*2.0; float b2=abs(fract(n2*3.0)-0.5)*2.0; float b3=abs(fract(n3*2.0)-0.5)*2.0;\n' +
    '        float l1=1.0-smoothstep(0.0,0.2,b1); float l2=1.0-smoothstep(0.0,0.2,b2); float l3=1.0-smoothstep(0.0,0.2,b3);\n' +
    '        return clamp(l1+l2*0.7+l3*0.4,0.0,1.0);\n' +
    '    }\n' +
    '    case 26: {\n' +
    '        vec2 f12=vor(p*1.5); float spot=1.0-smoothstep(0.0,0.25,f12.x);\n' +
    '        vec2 disp=vec2((sn(p*0.8)*2.0-1.0)*0.15,(sn(p*0.8+vec3(3.1,0.0,0.0))*2.0-1.0)*0.15);\n' +
    '        vec2 f12b=vor(p*1.5+vec3(disp,0.0)); float spot2=(1.0-smoothstep(0.0,0.2,f12b.x))*0.5;\n' +
    '        return clamp(0.88-spot*0.88-spot2*0.3,0.0,1.0);\n' +
    '    }\n' +
    '    case 27: {\n' +
    '        vec2 f12 = vor(p);\n' +
    '        return 1.0 - pow(clamp(1.0 - f12.x, 0.0, 1.0), 2.0);\n' +
    '    }\n' +
    '    case 28: {\n' +
    '        float base = fbmT(p, o, g, l);\n' +
    '        return 1.0 - base * 0.25;\n' +
    '    }\n' +
    '    case 29: {\n' +
    '        vec2 f12 = vor(p);\n' +
    '        float spot = 1.0 - smoothstep(0.0, 0.35, f12.x);\n' +
    '        return 1.0 - spot * 0.85;\n' +
    '    }\n' +
    '    case 30: {\n' +
    '        vec3 q=vec3(fbmG(p,o,g,l)-0.5,fbmG(p+vec3(5.2,1.3,2.8),o,g,l)-0.5,fbmG(p+vec3(1.7,9.2,4.1),o,g,l)-0.5);\n' +
    '        vec3 r=vec3(fbmG(p+q+vec3(8.3,2.8,5.6),o,g,l)-0.5,fbmG(p+q+vec3(2.1,5.4,8.9),o,g,l)-0.5,fbmG(p+q,o,g,l)-0.5);\n' +
    '        float base=fbmS(p+r*2.0,o,g,l);\n' +
    '        float grad=length(vec2(dFdx(base),dFdy(base)));\n' +
    '        float lines=1.0-smoothstep(0.0,0.018,grad*10.0);\n' +
    '        return mix(base,base*0.1,1.0-lines);\n' +
    '    }\n' +
    '    case 31: {\n' +
    '        vec2 f1=vor(p*1.0); vec2 f2=vor(p*2.3+vec3(1.7,0.0,0.0)); vec2 f3=vor(p*4.1+vec3(0.0,3.2,0.0));\n' +
    '        float spots=(1.0-smoothstep(0.0,0.3,f1.x))*0.6+(1.0-smoothstep(0.0,0.2,f2.x))*0.5+(1.0-smoothstep(0.0,0.15,f3.x))*0.3;\n' +
    '        float micro=(1.0-sn(p*6.0))*0.12;\n' +
    '        return clamp(1.0-spots-micro,0.0,1.0);\n' +
    '    }\n' +
    '    default: return 0.5;\n' +
    '    }\n' +
    '}\n' +
    'vec3 samplePalette(float t) {\n' +
    '    t = clamp(t, 0.0, 1.0);\n' +
    '    float s = t * 4.0;\n' +
    '    int i = int(min(s, 3.0));\n' +
    '    return mix(u_pal[i], u_pal[i + 1], s - float(i));\n' +
    '}\n' +
    'void main() {\n' +
    '    vec2 uv = gl_FragCoord.xy / u_res;\n' +
    '    vec2 correctedUV = vec2(uv.x * u_correction, uv.y);\n' +
    '    vec3 p;\n' +
    '    p.x = correctedUV.x * u_gScale * u_scale.x + u_ofs.x;\n' +
    '    p.y = correctedUV.y * u_gScale * u_scale.y + u_ofs.y;\n' +
    '    p.z = 0.0;\n' +
    '    p += u_loop;\n' +
    '    float sf = float(u_seed) * 7.1391;\n' +
    '    p += vec3(sf*0.37291, sf*0.58413, sf*0.19537);\n' +
    '    if (u_warp > 0.001) {\n' +
    '        vec3 wv = vec3(\n' +
    '            gn(p+vec3(3.7,1.2,0.5))*2.0-1.0,\n' +
    '            gn(p+vec3(0.7,5.3,2.1))*2.0-1.0,\n' +
    '            gn(p+vec3(2.3,0.8,6.7))*2.0-1.0\n' +
    '        );\n' +
    '        p += wv * u_warp;\n' +
    '    }\n' +
    '    float n = evalNoise(p, u_oct, u_gain, u_lac);\n' +
    '    if (u_abs==1) n = abs(n*2.0-1.0);\n' +
    '    if (u_cyc>0.001) n = fract(n*(1.0+u_cyc));\n' +
    '    if (u_hi > u_lo+0.001) n = clamp((n-u_lo)/(u_hi-u_lo),0.0,1.0);\n' +
    '    n = clamp(n + u_bri, 0.0, 1.0);\n' +
    '    float c = (u_con-0.5)*2.0;\n' +
    '    if (abs(c)>0.001) {\n' +
    '        if (c>0.0) {\n' +
    '            float e=1.0+c*4.0;\n' +
    '            n = (n<0.5) ? 0.5*pow(clamp(2.0*n,0.0,1.0),e)\n' +
    '                        : 1.0-0.5*pow(clamp(2.0*(1.0-n),0.0,1.0),e);\n' +
    '        } else {\n' +
    '            n = mix(0.5, n, 1.0+c);\n' +
    '        }\n' +
    '    }\n' +
    '    n = clamp(n,0.0,1.0);\n' +
    '    vec3 col = (u_col==0) ? vec3(n) : samplePalette(fract(n+u_cph));\n' +
    '    fragColor = vec4(col,1.0);\n' +
    '}\n';

  var gl = null;
  var glCanvas = null;
  var uniforms = {};
  var generation = 0;
  var greyscale = false;
  var currentStops = SIGNATURE_STOPS;
  var queue = [];
  var scheduled = false;

  function hexToRgb(hex) {
    var v = parseInt(hex.replace('#', ''), 16);
    return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
  }

  function initGL() {
    try {
      glCanvas = document.createElement('canvas');
      glCanvas.width = TILE_SIZE;
      glCanvas.height = TILE_SIZE;
      gl = glCanvas.getContext('webgl2', { antialias: false, preserveDrawingBuffer: true });
      if (!gl) return false;

      function compile(kind, src) {
        var s = gl.createShader(kind);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
          if (window.console && console.warn) console.warn('vss-noise-live shader: ' + gl.getShaderInfoLog(s));
          return null;
        }
        return s;
      }
      var vs = compile(gl.VERTEX_SHADER, VERT);
      var fs = compile(gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return false;

      var prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
      gl.useProgram(prog);

      var quad = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'vPosition');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

      ['u_res', 'u_loop', 'u_gScale', 'u_scale', 'u_type', 'u_oct', 'u_gain', 'u_lac',
       'u_seed', 'u_lo', 'u_hi', 'u_bri', 'u_con', 'u_abs', 'u_cyc', 'u_ofs', 'u_warp',
       'u_col', 'u_cph', 'u_correction', 'u_pal'].forEach(function (name) {
        uniforms[name] = gl.getUniformLocation(prog, name);
      });

      // Static defaults — identical to the plugin's kDefaults through ProcessOpenGL
      gl.viewport(0, 0, TILE_SIZE, TILE_SIZE);
      gl.uniform2f(uniforms.u_res, TILE_SIZE, TILE_SIZE);
      gl.uniform3f(uniforms.u_loop, 0.0, 0.0, 1.27323954474);
      gl.uniform2f(uniforms.u_scale, 2.05, 2.05);
      gl.uniform1i(uniforms.u_oct, 4);
      gl.uniform1f(uniforms.u_gain, 0.5);
      gl.uniform1f(uniforms.u_lac, 1.005);
      gl.uniform1i(uniforms.u_seed, 0);
      gl.uniform1f(uniforms.u_lo, 0.0);
      gl.uniform1f(uniforms.u_hi, 1.0);
      gl.uniform1f(uniforms.u_bri, 0.0);
      gl.uniform1f(uniforms.u_con, 0.5);
      gl.uniform1i(uniforms.u_abs, 0);
      gl.uniform1f(uniforms.u_cyc, 0.0);
      gl.uniform2f(uniforms.u_ofs, 0.0, 0.0);
      gl.uniform1f(uniforms.u_warp, 0.0);
      gl.uniform1f(uniforms.u_cph, 0.0);
      gl.uniform1f(uniforms.u_correction, 1.0);
      setPalette(currentStops, false);
      return true;
    } catch (e) {
      return false;
    }
  }

  function setPalette(stops, isGreyscale) {
    greyscale = !!isGreyscale;
    currentStops = stops || SIGNATURE_STOPS;
    if (!greyscale) {
      var flat = [];
      currentStops.forEach(function (hex) {
        var rgb = hexToRgb(hex.trim());
        flat.push(rgb[0], rgb[1], rgb[2]);
      });
      gl.uniform3fv(uniforms.u_pal, new Float32Array(flat));
    }
    gl.uniform1i(uniforms.u_col, greyscale ? 0 : 1);
  }

  function renderTile(tile) {
    var type = parseInt(tile.getAttribute('data-type'), 10);
    if (isNaN(type)) return;
    var slider = TYPE_GSCALE_SLIDER[type] !== undefined ? TYPE_GSCALE_SLIDER[type] : DEFAULT_GSCALE_SLIDER;
    gl.uniform1f(uniforms.u_gScale, 0.5 + slider * 19.5);
    gl.uniform1i(uniforms.u_type, type);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    var canvas = tile.__canvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.width = TILE_SIZE;
      canvas.height = TILE_SIZE;
      canvas.className = 'vss-lp__noise-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      tile.insertBefore(canvas, tile.firstChild);
      tile.__canvas = canvas;
      tile.__ctx = canvas.getContext('2d');
    }
    tile.__ctx.drawImage(glCanvas, 0, 0);
    tile.classList.add('is-live');
    tile.__gen = generation;
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      var start = performance.now();
      var count = 0;
      while (queue.length && count < MAX_TILES_PER_FRAME && performance.now() - start < FRAME_BUDGET_MS) {
        renderTile(queue.shift());
        count++;
      }
      scheduled = false;
      if (queue.length) schedule();
    });
  }

  function enqueue(tile) {
    if (tile.__gen === generation) return;
    if (queue.indexOf(tile) === -1) queue.push(tile);
    schedule();
  }

  function activate() {
    if (!initGL()) return;

    // Live mode confirmed: reveal the live line, enable swatches
    var line = section.querySelector('[data-noise-live-line]');
    if (line) line.removeAttribute('hidden');

    var swatches = Array.prototype.slice.call(section.querySelectorAll('[data-palette-swatch]'));
    var selectedName = section.querySelector('[data-palette-selected-name]');
    swatches.forEach(function (btn) {
      btn.removeAttribute('disabled');
      btn.addEventListener('click', function () {
        swatches.forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        if (selectedName) {
          var nameEl = btn.querySelector('.vss-lp__palette-name');
          if (nameEl) selectedName.textContent = nameEl.textContent;
        }
        generation++;
        if (btn.hasAttribute('data-greyscale')) {
          setPalette(null, true);
        } else {
          setPalette((btn.getAttribute('data-stops') || '').split(','), false);
        }
        // Re-render everything already live or on screen; unseen tiles pick
        // up the new palette when they scroll into view.
        queue.length = 0;
        tiles.forEach(function (tile) {
          if (tile.__gen !== undefined || tile.__visible) enqueue(tile);
        });
      });
    });

    // Render tiles on demand as they approach the viewport
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.__visible = entry.isIntersecting;
          if (entry.isIntersecting) enqueue(entry.target);
        });
      }, { rootMargin: '120px' });
      tiles.forEach(function (tile) { io.observe(tile); });
    } else {
      tiles.forEach(enqueue);
    }
  }

  // Initialise only when the section approaches the viewport — no GPU work
  // for visitors who never scroll this far.
  if ('IntersectionObserver' in window) {
    var sectionIo = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.disconnect();
          activate();
        }
      });
    }, { rootMargin: '400px' });
    sectionIo.observe(section);
  } else {
    activate();
  }
})();
